import React from 'react';
import { Alert as AntAlert, AlertProps as AntAlertProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface AlertProps extends AntAlertProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  alert: {
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    fontSize: theme.typography.fontSize.sm,
    borderRadius: theme.radius.sm,
    borderWidth: theme.border.width.sm,
    borderStyle: 'solid',
    lineHeight: theme.typography.lineHeight.normal,
    '& .ant-alert-message': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-alert-description': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      marginTop: theme.spacing[1],
    },
    '& .ant-alert-close-icon': {
      color: theme.color.semantic.text.secondary,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        color: theme.color.semantic.text.primary,
      },
    },
  },
  alertSuccess: {
    backgroundColor: theme.color.semantic.accent.green.subtle,
    borderColor: theme.color.semantic.accent.green.strong,
    color: theme.color.semantic.accent.green.strong,
    '& .ant-alert-icon': {
      color: theme.color.semantic.accent.green.strong,
    },
  },
  alertInfo: {
    backgroundColor: theme.color.semantic.brand.default.subtle,
    borderColor: theme.color.semantic.brand.default.primary,
    color: theme.color.semantic.brand.default.primary,
    '& .ant-alert-icon': {
      color: theme.color.semantic.brand.default.primary,
    },
  },
  alertWarning: {
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    borderColor: theme.color.semantic.accent.orange.strong,
    color: theme.color.semantic.accent.orange.strong,
    '& .ant-alert-icon': {
      color: theme.color.semantic.accent.orange.strong,
    },
  },
  alertError: {
    backgroundColor: theme.color.semantic.brand.danger.light,
    borderColor: theme.color.semantic.brand.danger.primary,
    color: theme.color.semantic.brand.danger.primary,
    '& .ant-alert-icon': {
      color: theme.color.semantic.brand.danger.primary,
    },
  },
  alertBanner: {
    borderRadius: theme.radius.none,
    width: '100%',
  },
}));

export const Alert: React.FC<AlertProps> = ({
  className = '',
  type = 'info',
  showIcon = true,
  closable = false,
  banner = false,
  ...rest
}) => {
  const classes = useStyles();

  const getTypeClass = () => {
    switch (type) {
      case 'success':
        return classes.alertSuccess;
      case 'warning':
        return classes.alertWarning;
      case 'error':
        return classes.alertError;
      case 'info':
      default:
        return classes.alertInfo;
    }
  };

  const combinedClassName = `${classes.alert} ${getTypeClass()} ${
    banner ? classes.alertBanner : ''
  } ${className}`.trim();

  return (
    <AntAlert
      className={combinedClassName}
      type={type}
      showIcon={showIcon}
      closable={closable}
      banner={banner}
      {...rest}
    />
  );
};

export default Alert;
